import { CHECKLIST_CHANGE_TYPES } from './appConstants';

const EMPTY_CHECKLIST_PAYLOAD = { checklists: [] };

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

export const normalizeChecklistPayload = (value) => {
  if (value && !Array.isArray(value) && Array.isArray(value.checklists)) {
    return {
      ...value,
      checklists: value.checklists.map(normalizeChecklistGroup)
    };
  }

  if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0]?.checklists)) {
    return normalizeChecklistPayload(value[0]);
  }

  if (Array.isArray(value)) {
    return {
      checklists: value.map(normalizeChecklistGroup)
    };
  }

  return { ...EMPTY_CHECKLIST_PAYLOAD };
};

const normalizeChecklistGroup = (checklist = {}, index = 0) => ({
  ...checklist,
  title: checklist.title || checklist.checklistTitle || `Checklist ${index + 1}`,
  position: checklist.position || index + 1,
  items: Array.isArray(checklist.items) ? checklist.items.map(normalizeChecklistItem) : []
});

const normalizeChecklistItem = (item = {}) => ({
  ...item,
  text: item.text || '',
  completed: Boolean(item.completed),
  assignedTo: item.assignedTo ?? item.assigned_to ?? null,
  dueDate: item.dueDate ?? item.due_date ?? null,
  priority: item.priority ?? null,
  position: item.position || null
});

const normalizeFlatItemForSave = (item = {}) => ({
  id: item.id,
  text: item.text || '',
  completed: Boolean(item.completed),
  assignedTo: item.assignedTo ?? item.assigned_to ?? null,
  dueDate: item.dueDate ?? item.due_date ?? null,
  priority: item.priority ?? null,
  position: item.position || null
});

const getItemIdentity = (item = {}, checklistTitle = '') => ({
  id: item.id ? String(item.id) : '',
  checklistTitle: item.checklistTitle || checklistTitle || '',
  text: item.text || '',
  position: item.position || null
});

const isSameItem = (candidate, identity, checklistTitle) => {
  if (!candidate || !identity) return false;
  if (identity.id && candidate.id && String(candidate.id) === identity.id) return true;

  const sameTitle = (identity.checklistTitle || '') === (checklistTitle || '');
  const sameText = (identity.text || '') === (candidate.text || '');
  const samePosition = identity.position && candidate.position && Number(identity.position) === Number(candidate.position);

  return sameTitle && sameText && samePosition;
};

const findItemLocation = (payload, identity) => {
  const fallbackMatches = [];

  for (let checklistIndex = 0; checklistIndex < payload.checklists.length; checklistIndex += 1) {
    const checklist = payload.checklists[checklistIndex];
    const itemIndex = (checklist.items || []).findIndex((item) => isSameItem(item, identity, checklist.title));
    if (itemIndex !== -1) {
      return { checklistIndex, itemIndex };
    }

    if (identity?.text && identity?.checklistTitle === checklist.title) {
      (checklist.items || []).forEach((item, index) => {
        if (item.text === identity.text) {
          fallbackMatches.push({ checklistIndex, itemIndex: index });
        }
      });
    }
  }

  return fallbackMatches.length === 1 ? fallbackMatches[0] : null;
};

const findChecklistIndex = (payload, checklistTitle) => (
  payload.checklists.findIndex((checklist) => checklist.title === checklistTitle)
);

const normalizePositions = (payload) => ({
  ...payload,
  checklists: payload.checklists.map((checklist, checklistIndex) => ({
    ...checklist,
    position: checklistIndex + 1,
    items: (checklist.items || []).map((item, itemIndex) => ({
      ...item,
      position: itemIndex + 1
    }))
  }))
});

const addItemToLatest = (latestPayload, changeMeta) => {
  const items = (Array.isArray(changeMeta.items) && changeMeta.items.length > 0
    ? changeMeta.items
    : [changeMeta.item]
  ).filter(Boolean).map(normalizeFlatItemForSave);
  const checklistTitle = changeMeta.checklistTitle || changeMeta.item?.checklistTitle || changeMeta.items?.[0]?.checklistTitle || 'Default Checklist';
  const nextPayload = {
    ...latestPayload,
    checklists: latestPayload.checklists.map((checklist) => ({
      ...checklist,
      items: [...(checklist.items || [])]
    }))
  };

  let checklistIndex = findChecklistIndex(nextPayload, checklistTitle);
  if (checklistIndex === -1) {
    nextPayload.checklists.push({
      id: changeMeta.checklistId,
      title: checklistTitle,
      position: nextPayload.checklists.length + 1,
      items: []
    });
    checklistIndex = nextPayload.checklists.length - 1;
  }

  items.forEach((item) => {
    const duplicateIdentity = getItemIdentity(item, checklistTitle);
    const alreadyExists = nextPayload.checklists[checklistIndex].items.some((existingItem) => (
      isSameItem(existingItem, duplicateIdentity, checklistTitle)
    ));
    if (!alreadyExists) {
      nextPayload.checklists[checklistIndex].items.push(item);
    }
  });

  return normalizePositions(nextPayload);
};

const updateItemInLatest = (latestPayload, changeMeta) => {
  const item = normalizeFlatItemForSave(changeMeta.item);
  const identity = changeMeta.identity || getItemIdentity(changeMeta.previousItem || item, changeMeta.checklistTitle);
  const location = findItemLocation(latestPayload, identity);

  if (!location) return latestPayload;

  const nextPayload = {
    ...latestPayload,
    checklists: latestPayload.checklists.map((checklist, checklistIndex) => {
      if (checklistIndex !== location.checklistIndex) return checklist;
      return {
        ...checklist,
        items: (checklist.items || []).map((existingItem, itemIndex) => (
          itemIndex === location.itemIndex
            ? { ...existingItem, ...item, id: existingItem.id || item.id }
            : existingItem
        ))
      };
    })
  };

  return normalizePositions(nextPayload);
};

const deleteItemFromLatest = (latestPayload, changeMeta) => {
  const identity = changeMeta.identity || getItemIdentity(changeMeta.item, changeMeta.checklistTitle);
  const location = findItemLocation(latestPayload, identity);

  if (!location) return latestPayload;

  const nextPayload = {
    ...latestPayload,
    checklists: latestPayload.checklists.map((checklist, checklistIndex) => {
      if (checklistIndex !== location.checklistIndex) return checklist;
      return {
        ...checklist,
        items: (checklist.items || []).filter((_, itemIndex) => itemIndex !== location.itemIndex)
      };
    }).filter((checklist) => (checklist.items || []).length > 0)
  };

  return normalizePositions(nextPayload);
};

const deleteChecklistFromLatest = (latestPayload, changeMeta) => normalizePositions({
  ...latestPayload,
  checklists: latestPayload.checklists.filter((checklist) => checklist.title !== changeMeta.checklistTitle)
});

const reorderLatest = (latestPayload, submittedPayload) => {
  const submitted = normalizeChecklistPayload(submittedPayload);
  const nextChecklists = submitted.checklists.map((submittedChecklist, checklistIndex) => {
    const latestChecklist = latestPayload.checklists.find((checklist) => checklist.title === submittedChecklist.title);
    const latestItems = latestChecklist?.items || [];
    const orderedItems = [];
    const usedLatestIndexes = new Set();

    (submittedChecklist.items || []).forEach((submittedItem) => {
      const identity = getItemIdentity(submittedItem, submittedChecklist.title);
      const latestIndex = latestItems.findIndex((latestItem, index) => (
        !usedLatestIndexes.has(index) && isSameItem(latestItem, identity, submittedChecklist.title)
      ));

      if (latestIndex === -1) {
        orderedItems.push(submittedItem);
      } else {
        usedLatestIndexes.add(latestIndex);
        orderedItems.push({ ...latestItems[latestIndex], ...submittedItem, id: latestItems[latestIndex].id || submittedItem.id });
      }
    });

    latestItems.forEach((latestItem, latestIndex) => {
      if (!usedLatestIndexes.has(latestIndex)) {
        orderedItems.push(latestItem);
      }
    });

    return {
      ...(latestChecklist || submittedChecklist),
      title: submittedChecklist.title,
      position: checklistIndex + 1,
      items: orderedItems
    };
  });

  latestPayload.checklists.forEach((latestChecklist) => {
    if (!nextChecklists.some((checklist) => checklist.title === latestChecklist.title)) {
      nextChecklists.push(latestChecklist);
    }
  });

  return normalizePositions({ checklists: nextChecklists });
};

export const mergeChecklistChange = (latestChecklist, submittedChecklist, changeMeta = {}) => {
  const latestPayload = normalizeChecklistPayload(latestChecklist);

  switch (changeMeta.type) {
    case CHECKLIST_CHANGE_TYPES.ADD_ITEM:
      return addItemToLatest(latestPayload, changeMeta);
    case CHECKLIST_CHANGE_TYPES.UPDATE_ITEM:
      return updateItemInLatest(latestPayload, changeMeta);
    case CHECKLIST_CHANGE_TYPES.DELETE_ITEM:
      return deleteItemFromLatest(latestPayload, changeMeta);
    case CHECKLIST_CHANGE_TYPES.DELETE_CHECKLIST:
      return deleteChecklistFromLatest(latestPayload, changeMeta);
    case CHECKLIST_CHANGE_TYPES.CLEAR_ALL:
      return { ...EMPTY_CHECKLIST_PAYLOAD };
    case CHECKLIST_CHANGE_TYPES.REORDER:
      return reorderLatest(latestPayload, submittedChecklist);
    case CHECKLIST_CHANGE_TYPES.REPLACE_ALL:
      return normalizeChecklistPayload(submittedChecklist);
    default:
      if (hasOwn(changeMeta, 'type')) return latestPayload;
      return normalizeChecklistPayload(submittedChecklist);
  }
};
